import { createApi } from '@reduxjs/toolkit/query/react';
import { HttpMethod } from '@vision/ui/enums';
import { buildEndpointWithQuery } from '@vision/ui/utils';
import { ApiResponse, HttpBaseRequest, SchemaField, SimpleTagGroupItem } from '../interfaces';
import { axiosBaseQuery } from './base.service';
import { buildGetSchemaFieldsV2Request, GetSchemaFieldsV2Request } from './schema-fields-api.routes';

export const SchemaFieldsService = createApi({
  reducerPath: 'SchemaFieldsService',
  baseQuery: axiosBaseQuery(),
  endpoints: (builder) => ({
    getSchemaFields: builder.query<
      SchemaField[],
      HttpBaseRequest<{ accountId: string; query?: string; type?: string }>
    >({
      query: ({ accountId, query, type }) => ({
        url: buildEndpointWithQuery(
          `/v1/accounts/${accountId}/schema_fields`,
          [query, type ? `type=${encodeURIComponent(type)}` : ''].filter(Boolean).join('&'),
        ),
        method: HttpMethod.GET,
      }),
    }),
    getSchemaFieldsV2: builder.query<ApiResponse<SchemaField[]>, GetSchemaFieldsV2Request>({
      query: buildGetSchemaFieldsV2Request,
    }),
    getSchemaFieldValues: builder.query<
      ApiResponse<SimpleTagGroupItem[]>,
      HttpBaseRequest<{ accountId: string; schemaFieldId: string; query?: string }>
    >({
      query: ({ accountId, schemaFieldId, query }) => ({
        url: buildEndpointWithQuery(`/v2/accounts/${accountId}/schema_fields/${schemaFieldId}/values`, query),
        method: HttpMethod.GET,
      }),
    }),
    getSchemaTagValues: builder.query<
      ApiResponse<SimpleTagGroupItem[]>,
      HttpBaseRequest<{ accountId: string; schemaFieldIds?: string[]; query?: string }>
    >({
      query: ({ accountId, schemaFieldIds, query }) => {
        const schemaFieldQuery = schemaFieldIds?.length ? `schema_field_ids=${schemaFieldIds.join(',')}` : '';
        const nextQuery = [query, schemaFieldQuery].filter(Boolean).join('&');

        return {
          url: buildEndpointWithQuery(`/v2/accounts/${accountId}/schema_fields/values`, nextQuery),
          method: HttpMethod.GET,
        };
      },
    }),
    getSchemaFieldsValues: builder.query<
      ApiResponse<SimpleTagGroupItem[]>,
      HttpBaseRequest<{ accountId: string; query?: string }>
    >({
      query: ({ accountId, query }) => ({
        url: buildEndpointWithQuery(`/v2/accounts/${accountId}/schema_fields/values`, query),
        method: HttpMethod.GET,
      }),
    }),
    createSchemaField: builder.mutation<SchemaField, HttpBaseRequest<{ accountId: string; data: SchemaField }>>({
      query: ({ accountId, data }) => ({
        url: `/v1/accounts/${accountId}/schema_fields`,
        method: HttpMethod.POST,
        data,
      }),
    }),
    updateSchemaField: builder.mutation<SchemaField, HttpBaseRequest<{ accountId: string; data: SchemaField }>>({
      query: ({ accountId, data }) => ({
        url: `/v1/accounts/${accountId}/schema_fields/${data.id}/update`,
        method: HttpMethod.POST,
        data,
      }),
    }),
  }),
});

export const {
  useGetSchemaFieldsQuery,
  useGetSchemaFieldsV2Query,
  useLazyGetSchemaFieldsV2Query,
  useGetSchemaFieldValuesQuery,
  useLazyGetSchemaTagValuesQuery,
  useGetSchemaFieldsValuesQuery,
  useLazyGetSchemaFieldsQuery,
  useLazyGetSchemaFieldValuesQuery,
  useLazyGetSchemaFieldsValuesQuery,
  useCreateSchemaFieldMutation,
  useUpdateSchemaFieldMutation,
} = SchemaFieldsService;
